import { isFormsTabDirty } from "../../components/design-tools/flows/process-editor/forms-tab-dirty-state";
import { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import { DesignerChangeKind } from "./designer-live-types";

/**
 * Pure routing rules for designer-live change events (Board change-event-routing emsal):
 * for each kind, WHICH provider reload keeps everyone on the same data, and WHEN the
 * reload must be deferred because the user is mid-edit in that tool (hybrid refresh —
 * a silent reload would clobber their open editor's draft).
 *
 * Kept free of side effects so vitest pins the whole table.
 */

/**
 * Session variables have no provider-level store (the Datasources panel owns the list
 * locally), so their reload is a window CustomEvent the panel subscribes to.
 */
export const SESSION_VARIABLES_CHANGED_EVENT: string = "kuika-designer-live-session-variables-changed";

/** The instance-typed surface the rules need (the ScreenDesignContextProvider). */
export interface IDesignerLiveSdAccess {
  projectProvider: {
    loadActionDefinitions: (dontFilterByProjectType?: boolean, skipEagerTableFetch?: boolean) => Promise<void> | void;
    refreshProjectFromServer: (projectId: string) => Promise<void>;
  };
  datasourceProvider: {
    refreshDatabases: () => Promise<void> | void;
    refreshTableIds: () => Promise<void> | void;
    refreshTables: (excludedIds?: string[]) => Promise<void> | void;
    refreshOfflineTables: () => Promise<void> | void;
  };
  designSituationProvider: { updateDatabaseDiagramData: () => Promise<void> | void };
  apiManagementProvider: { loadApiManagements: (projectId: string) => Promise<void> | void };
  workflowProvider: { loadWorkFlowList: (projectId: string) => Promise<void> | void };
  scheduledJobsProvider: { loadScheduledJobs: (projectId: string) => Promise<void> | void };
  notificationTemplateProvider: { getNotificationTemplates: () => Promise<void> | void };
  configManagerProvider: {
    fetchConfigs: (options: { shouldLockPage: boolean; shouldFetchGlobals: boolean }) => Promise<void> | void;
    fetchConfigLocalizations: () => Promise<void> | void;
  };
  screenProvider: { setScreenList: () => Promise<void> | void };
  branchingProvider: { getBranches: () => Promise<void> | void };
  state: IScreenDesignContextState;
}

export function reloadForKind(kind: DesignerChangeKind, sd: IDesignerLiveSdAccess, projectId: string): Promise<void> {
  switch (kind) {
    case "datasource.changed":
      return Promise.all([sd.datasourceProvider.refreshDatabases(), sd.datasourceProvider.refreshTableIds()]).then(
        () => undefined
      );
    case "schema.changed":
      return Promise.all([
        sd.designSituationProvider.updateDatabaseDiagramData(),
        sd.datasourceProvider.refreshTables()
      ]).then(() => undefined);
    case "action.changed":
      return Promise.resolve(sd.projectProvider.loadActionDefinitions(undefined, true)).then(() => undefined);
    case "api.changed":
      return Promise.resolve(sd.apiManagementProvider.loadApiManagements(projectId)).then(() => undefined);
    case "workflow.changed":
    case "workflow.lockChanged":
      return Promise.resolve(sd.workflowProvider.loadWorkFlowList(projectId)).then(() => undefined);
    case "scheduledJob.changed":
      return Promise.resolve(sd.scheduledJobsProvider.loadScheduledJobs(projectId)).then(() => undefined);
    case "notificationTemplate.changed":
      return Promise.resolve(sd.notificationTemplateProvider.getNotificationTemplates()).then(() => undefined);
    case "config.changed":
      return Promise.resolve(
        sd.configManagerProvider.fetchConfigs({ shouldLockPage: false, shouldFetchGlobals: true })
      ).then(() => undefined);
    case "configLocalization.changed":
      return Promise.resolve(sd.configManagerProvider.fetchConfigLocalizations()).then(() => undefined);
    case "sessionVariable.changed":
      window.dispatchEvent(new CustomEvent(SESSION_VARIABLES_CHANGED_EVENT));
      return Promise.resolve();
    case "offlineTable.changed":
      return Promise.resolve(sd.datasourceProvider.refreshOfflineTables()).then(() => undefined);
    case "screenList.changed":
      return Promise.resolve(sd.screenProvider.setScreenList()).then(() => undefined);
    case "branch.changed":
      return Promise.resolve(sd.branchingProvider.getBranches()).then(() => undefined);
    case "project.changed":
      // refreshProjectFromServer WRITES the fetched project into state (with the
      // prevProjectRevision baseline) — getProjectFromServer alone only returned it,
      // which made this reload silently inert.
      return sd.projectProvider.refreshProjectFromServer(projectId);
    case "workflowScreens.changed":
      // Server-side automated screen creation (Workflow Screens wizard): the write-set
      // spans the screen list AND the project json (processStepScreenIds drives the
      // WorkflowScreens grouping, the wizard's linked-screen view, and the ResumeProcess
      // parameter panels) AND the untriggered-process bookkeeping — refresh all three.
      return Promise.all([
        sd.projectProvider.refreshProjectFromServer(projectId),
        sd.screenProvider.setScreenList(),
        sd.workflowProvider.loadWorkFlowList(projectId)
      ]).then(() => undefined);
    default:
      return Promise.resolve();
  }
}

/**
 * Hybrid-refresh gate: is the user actively editing inside the tool this kind belongs to?
 * True → the reload is DEFERRED (toast + toolbar badge; applied when the editor closes).
 * The predicates read the same provider state the editors themselves key off.
 */
export function isToolBusy(kind: DesignerChangeKind, state: IDesignerLiveSdAccess["state"]): boolean {
  switch (kind) {
    case "datasource.changed":
    case "schema.changed":
    case "action.changed":
      return Boolean(state.activeEditingDatasource) || Boolean(state.editingAction);
    case "api.changed":
      return Boolean(state.selectedApiId);
    case "workflow.changed":
    case "workflow.lockChanged":
      // The BPMN editor has its own pessimistic lock; the list reload is always safe.
      return false;
    case "scheduledJob.changed":
      return Boolean(state.selectedSjId);
    case "notificationTemplate.changed":
      return Boolean(state.editingNotificationTemplate) || Boolean(state.creatingNotificationTemplate);
    case "config.changed":
      return Boolean(state.activeConfiguration) || Boolean(state.isCreateConfigActive);
    case "sessionVariable.changed":
      // The panel refetches when the editor closes anyway (editingSessionVariables watcher),
      // so defer while the session-variables editor is open.
      return Boolean(state.editingSessionVariables);
    case "configLocalization.changed":
    case "offlineTable.changed":
      return false;
    case "screenList.changed":
    case "branch.changed":
    case "project.changed":
      return false;
    case "workflowScreens.changed":
      // TWO surfaces edit workflow screens, and BOTH have to count as busy:
      //
      //  * the retired wizard modal — it resets its local state from props only on the open
      //    transition, so a reload while it is open would be invisible to it;
      //  * the Forms tab, which is where this work actually happens now. It holds unapplied
      //    edits, created drafts and pending screen assignments, and a persisted step↔screen
      //    binding OUTRANKS a local draft — so a remote apply landing mid-edit silently
      //    replaced the user's draft with someone else's screen. The gate used to ask only
      //    about the modal, which meant the live surface always read as idle.
      return Boolean(state.isWorkflowScreenStepWizardOpen) || isFormsTabDirty();
    default:
      return false;
  }
}

/** Toolbar tab a deferred change belongs to — feeds the per-tab badge. */
export function toolbarTabOf(kind: DesignerChangeKind): string {
  switch (kind) {
    case "datasource.changed":
    case "schema.changed":
    case "sessionVariable.changed":
    case "offlineTable.changed":
      return "datasources";
    case "action.changed":
      return "ai-hub";
    case "api.changed":
      return "api-management";
    case "workflow.changed":
    case "workflow.lockChanged":
    case "workflowScreens.changed":
      return "process-automation";
    case "scheduledJob.changed":
      return "scheduled-jobs";
    case "notificationTemplate.changed":
      return "email-builder";
    case "config.changed":
    case "configLocalization.changed":
      return "config-manager";
    case "branch.changed":
      return "branching";
    case "screenList.changed":
    case "project.changed":
      return "ui-design";
    default:
      return "ui-design";
  }
}
